import React from "react";
import MuiButton from "@mui/material/Button";
import type { ButtonProps as MuiButtonProps } from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import type {
  BaseAtomProps,
  ComponentColor,
  ComponentSize,
} from "@/components/types";

export interface ButtonProps
  extends Omit<MuiButtonProps, "color" | "size">,
    BaseAtomProps {
  /**
   * Button variant following MUI design system
   */
  variant?: "text" | "outlined" | "contained";

  /**
   * Button color scheme
   */
  color?: ComponentColor;

  /**
   * Button size with mobile-first considerations
   */
  size?: ComponentSize;

  /**
   * Disabled state
   */
  disabled?: boolean;

  /**
   * Loading state with spinner
   */
  loading?: boolean;

  /**
   * Click handler
   */
  onClick?: () => void;

  /**
   * Button type for forms
   */
  type?: "button" | "submit" | "reset";

  /**
   * Full width button
   */
  fullWidth?: boolean;

  /**
   * Start icon
   */
  startIcon?: React.ReactNode;

  /**
   * End icon
   */
  endIcon?: React.ReactNode;
}

const ButtonComponent: React.FC<ButtonProps> = ({
  variant = "contained",
  color = "primary",
  size = "medium",
  disabled = false,
  loading = false,
  onClick,
  type = "button",
  fullWidth = false,
  startIcon,
  endIcon,
  className,
  "data-testid": _dataTestId,
  children,
  ...props
}) => {
  const theme = useTheme();

  // Memoize the sx styles to prevent unnecessary re-renders
  const buttonStyles = React.useMemo(() => {
    const baseStyles = {
      height: 50,
      borderRadius: 3,
      textTransform: "none" as const,
      fontWeight: 600,
      fontSize: 16,
      transition: "all 0.2s ease-in-out",
    };

    // Color-specific styles
    const colorStyles = {
      primary: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        boxShadow: `0px 2px 4px 0px ${theme.palette.primary.main}30`,
        "&:hover": {
          backgroundColor: theme.palette.primary.dark,
          boxShadow: `0px 4px 8px 0px ${theme.palette.primary.main}40`,
        },
      },
      secondary: {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        boxShadow: `0px 2px 4px 0px ${theme.palette.secondary.main}30`,
        "&:hover": {
          backgroundColor: theme.palette.secondary.dark,
          boxShadow: `0px 4px 8px 0px ${theme.palette.secondary.main}40`,
        },
      },
      success: {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.success.contrastText,
        boxShadow: `0px 2px 4px 0px ${theme.palette.success.main}30`,
        "&:hover": {
          backgroundColor: theme.palette.success.dark,
          boxShadow: `0px 4px 8px 0px ${theme.palette.success.main}40`,
        },
      },
      error: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        boxShadow: `0px 2px 4px 0px ${theme.palette.error.main}30`,
        "&:hover": {
          backgroundColor: theme.palette.error.dark,
          boxShadow: `0px 4px 8px 0px ${theme.palette.error.main}40`,
        },
      },
      warning: {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
        boxShadow: `0px 2px 4px 0px ${theme.palette.warning.main}30`,
        "&:hover": {
          backgroundColor: theme.palette.warning.dark,
          boxShadow: `0px 4px 8px 0px ${theme.palette.warning.main}40`,
        },
      },
      info: {
        backgroundColor: theme.palette.info.main,
        color: theme.palette.info.contrastText,
        boxShadow: `0px 2px 4px 0px ${theme.palette.info.main}30`,
        "&:hover": {
          backgroundColor: theme.palette.info.dark,
          boxShadow: `0px 4px 8px 0px ${theme.palette.info.main}40`,
        },
      },
    };

    // Size-specific styles
    const sizeStyles = {
      small: {
        height: 36,
        fontSize: 14,
        padding: "6px 16px",
      },
      medium: {
        height: 50,
        fontSize: 16,
        padding: "8px 24px",
      },
      large: {
        height: 64,
        fontSize: 18,
        padding: "12px 32px",
      },
    };

    // Disabled state styles
    const disabledStyles = {
      "&:disabled": {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.action.disabled,
        boxShadow: "none",
        cursor: "not-allowed",
      },
    };

    // Loading state styles
    const loadingStyles = loading
      ? {
          position: "relative" as const,
          color: "transparent",
          "&::after": {
            content: '""',
            position: "absolute" as const,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "20px",
            height: "20px",
            border: "2px solid currentColor",
            borderTop: "2px solid transparent",
            borderRadius: "50%",
            animation: "button-loading-spin 1s linear infinite",
          },
          "@keyframes button-loading-spin": {
            "0%": {
              transform: "translate(-50%, -50%) rotate(0deg)",
            },
            "100%": {
              transform: "translate(-50%, -50%) rotate(360deg)",
            },
          },
        }
      : {};

    return {
      ...baseStyles,
      ...colorStyles[color as keyof typeof colorStyles],
      ...sizeStyles[size as keyof typeof sizeStyles],
      ...disabledStyles,
      ...loadingStyles,
    };
  }, [theme, color, size, loading]);

  const muiButtonProps: MuiButtonProps = {
    variant,
    color: color as MuiButtonProps["color"],
    size,
    disabled: disabled || loading,
    onClick,
    type,
    fullWidth,
    startIcon: loading ? undefined : startIcon,
    endIcon: loading ? undefined : endIcon,
    className,
    sx: buttonStyles,
    ...props,
  };

  return <MuiButton {...muiButtonProps}>{children}</MuiButton>;
};

// Apply memoization with performance monitoring
export const Button = ButtonComponent;

// Set display name for debugging
Button.displayName = "Button";
